/**
 * Jetons de style — **façade TypeScript sur la palette de la refonte
 * « Nourri »** (`styles/tokens-nourri.css`, qui recopie
 * `design/design-tokens.md`).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 CE QUI A CHANGÉ, ET POURQUOI CE FICHIER SURVIT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Il pointait sur les noms de l'ancien système (`--accent`, `--ink-2`,
 * `--border`…), qu'un **pont** d'alias faisait suivre la refonte. Ce pont a été
 * déposé : la façade lit désormais les jetons de la refonte directement, et il
 * n'existe plus qu'**une** palette dans l'application.
 *
 * ⚠️ **Ne pas écrire de neuf contre ce fichier.** Un écran nouveau emploie les
 * composants de `components/nourri/` et les classes de
 * `styles/nourri-screens.css` ; les couleurs y vivent en CSS, pas en
 * TypeScript. Cette façade ne subsiste que pour la poignée d'endroits où une
 * couleur doit passer par une propriété d'élément :
 *
 *   - un attribut de présentation **SVG** (`WeightChart` : `fill`, `stroke`) ;
 *   - une propriété que le CSS ne peut pas atteindre depuis l'extérieur
 *     (`accentColor` d'une case à cocher) ;
 *   - une valeur calculée en JavaScript (`fillFor`, la teinte d'une jauge).
 *
 * Elle a fondu de vingt entrées à onze : les autres n'avaient plus de
 * consommateur, et une façade qui expose ce que personne n'emploie finit par
 * faire croire que c'est disponible.
 *
 * ⚠️ **Une `var()` n'est pas une chaîne de couleur.** Trois conséquences,
 * toutes rencontrées en migrant :
 *
 *   1. On ne peut pas la **concaténer** avec un suffixe d'alpha
 *      (`${TOKENS.accent}22` produirait `var(--brand)22`, invalide). Pour une
 *      couleur dérivée : `color-mix(in srgb, var(--brand) 12%, transparent)`.
 *   2. On ne peut pas la lire pour **calculer** en JavaScript. Aucun code ne le
 *      fait ; ne pas commencer.
 *   3. Elle ne va pas dans un **attribut** de présentation SVG (`fill="…"`) de
 *      façon fiable — d'où `style={{ fill }}` dans `WeightChart`, où la
 *      substitution est garantie.
 */

import type { MacroKey } from '../../lib/macros';
import type { MacroStatus } from '../../lib/macroStatus';

export const TOKENS = {
  /** Texte courant. */
  text: 'var(--ink)',
  /**
   * Texte secondaire — libellés, aides, unités.
   *
   * ⚠️ Préférer ce jeton à une `opacity:` posée à la main sur du texte : une
   * opacité ne peut pas convenir à deux fonds, et la valeur du jeton est
   * mesurée une fois pour toutes.
   */
  textQuiet: 'var(--ink-soft)',
  /**
   * 🔴 **Troisième rang = le même que le second, et ce n'est pas un oubli.**
   * La refonte n'a que deux encres lisibles (`--ink`, `--ink-soft`) ;
   * `--ink-faint` vaut **2,50:1** sur surface et ne convient qu'au décor. Le
   * jour où le système gagne un vrai troisième rang, c'est ici qu'il se pose.
   */
  textFaint: 'var(--ink-soft)',
  /** Décor seulement — séparateurs, glyphes `aria-hidden`. Jamais de texte. */
  textDecor: 'var(--ink-faint)',

  border: 'var(--line)',
  surface: 'var(--surface)',
  /**
   * ⚠️ La refonte n'a **qu'une** surface de carte : ici c'est l'ombre qui
   * détache, plus la teinte. Les deux entrées coexistent le temps que les
   * derniers appelants disparaissent.
   */
  surfaceRaised: 'var(--surface)',

  accent: 'var(--brand)',
  /** Ce qui se pose **sur** un aplat plein. */
  onAccent: 'var(--on-solid)',
  /**
   * 🔴 L'accent **en texte**. `--brand` est validé comme aplat et ne tient pas
   * les 4,5:1 d'un libellé ; `--brand-dark` est littéralement « la marque en
   * texte sur fond clair » (`design-tokens.md`).
   */
  accentInk: 'var(--brand-dark)',

  /**
   * ⚠️ `--warn` **est** le rouge de dépassement de la refonte : il n'y a plus
   * d'ambre d'avertissement, et `danger` comme `warn` désignent la même teinte.
   */
  danger: 'var(--warn)',
  /** « Atteint » — le vert de la marque. */
  ok: 'var(--brand)',

  errorBg: 'var(--warn-tint)',
  /**
   * 🔴 **Encre `--ink` sur le fond teinté, pas une teinte rouge.** Aucune
   * teinte du système ne tient 4,5:1 sur sa propre teinte claire. Mesure faite
   * pour `Badge`, valable partout.
   */
  errorInk: 'var(--ink)',
} as const;

/**
 * Code couleur des quatre macros.
 *
 * 🔴 **Ces teintes sont FIXES et valent partout où une macro apparaît** —
 * anneau, barres, fiches d'aliment (`design-tokens.md`, principe 2). Ne jamais
 * les réassigner par écran : c'est cette constance qui rend l'application
 * lisible d'un coup d'œil.
 *
 * ⚠️ Les calories n'ont pas de teinte propre : elles portent `--brand`.
 *
 * ⚠️ La couleur reste un **repère secondaire**. Chaque jauge porte son libellé
 * texte, et cette condition n'est pas facultative.
 */
export const MACRO_COLORS = {
  protein: 'var(--protein)',
  carbs: 'var(--carbs)',
  fat: 'var(--fat)',
  kcal: 'var(--brand)',
} as const;

/**
 * Teinte d'une jauge de macro.
 *
 * 🔴 **Elle ne dépend plus de l'état** — décidé après essai sur l'appareil :
 * ni rouge au dépassement, ni vert à l'objectif atteint. Les deux faisaient
 * porter à la couleur un jugement que l'application n'a pas à porter (specs
 * §6.2). Le **mot** (« restant » / « de trop ») et les chiffres disent l'état.
 *
 * ⚠️ Le paramètre `status` est conservé : c'est ici, et nulle part ailleurs,
 * qu'on rouvrirait la question si l'usage la reposait. Le supprimer obligerait
 * à retrouver tous les appelants pour la rouvrir.
 *
 * ⚠️ **Ici et pas dans `MacroMeter.tsx`** : `react-refresh` interdit à un
 * module exportant des composants d'exporter autre chose.
 */
export function fillFor(_status: MacroStatus, macro: MacroKey): string {
  return MACRO_COLORS[macro];
}
